import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Shield, Mail, Lock, ArrowRight, AlertTriangle, RefreshCw } from 'lucide-react';
import logoImg from '@/assets/Sthitih_Logo.png';
import { useAuthStore } from '@/stores/authStore';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuthStore();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!email || !password) {
      setError('Please enter both email and password.');
      return;
    }

    setLoading(true);
    const success = await (login as any)(email, password, rememberMe);
    setLoading(false);

    if (success) {
      navigate('/dashboard');
    } else {
      setError('Invalid credentials.');
    }
  };

  return (
    <div className="min-h-screen flex flex-col justify-center items-center p-6 relative overflow-hidden bg-pageBg">
      {/* Ambient Glows */}
      <div className="absolute top-[-10%] left-[-10%] w-[40vw] h-[40vw] rounded-full bg-forest/10 blur-[120px]" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[35vw] h-[35vw] rounded-full bg-walnut/10 blur-[100px]" />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="w-full max-w-md z-10"
      >
        {/* Header & Logo */}
        <div className="flex flex-col items-center mb-8 text-center">
          <div className="bg-white border border-softBorder p-3 rounded-2xl shadow-earth-sm mb-4">
            <img src={logoImg} alt="Svavas Logo" className="h-14 w-auto object-contain" />
          </div>
          <h2 className="text-3xl font-extrabold text-charcoal">Svavas Super Admin</h2>
          <p className="text-xs font-bold text-mutedSubheading tracking-wider uppercase mt-1">
            Centralized Platform Access
          </p>
        </div>

        {/* Login Card */}
        <div className="bg-cardBg border border-cardBorder p-8 rounded-card shadow-earth space-y-6">
          <div className="text-center">
            <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-warmSand rounded-full border border-softBorder text-xs font-bold text-forest">
              <Shield className="w-3.5 h-3.5" /> Portal Authentication
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1">
              <label className="text-xs font-bold text-mutedSubheading uppercase tracking-wider block">
                Super Admin Email
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-iconInactive">
                  <Mail className="w-4 h-4" />
                </span>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@example.com"
                  className="w-full pl-10 pr-4 py-3 glass-input text-sm font-semibold"
                />
              </div>
            </div>

            <div className="space-y-1">
              <label className="text-xs font-bold text-mutedSubheading uppercase tracking-wider block">
                Password
              </label>
              <div className="relative">
                <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-iconInactive">
                  <Lock className="w-4 h-4" />
                </span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 glass-input text-sm font-semibold"
                />
              </div>
            </div>

            <div className="flex items-center justify-between text-xs font-bold text-mutedSubheading pt-1">
              <label className="flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="w-4 h-4 rounded border-softBorder text-forest focus:ring-forest cursor-pointer"
                />
                <span>Remember me for 30 days</span>
              </label>
              <span className="text-[10px] uppercase font-black px-2 py-0.5 bg-warmSand rounded border border-softBorder text-forest">
                {rememberMe ? 'Extended 30D' : '12H Expiry'}
              </span>
            </div>

            {error && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="p-3 bg-[#FCE8E8] border border-[#D64545]/20 rounded-xl text-xs font-bold text-[#D64545] flex items-center gap-2"
              >
                <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                <span>{error}</span>
              </motion.div>
            )}

            <button
              disabled={loading}
              type="submit"
              className="w-full btn-primary flex justify-center items-center gap-2 cursor-pointer"
            >
              {loading ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  Sign In <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>
          </form>
        </div>
      </motion.div>
    </div>
  );
};
